import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { profileApi } from '@/lib/api/profile';
import { onProfileMutationSuccess } from '@/lib/profile-query';
import {
  LanguageProficiencyValues,
  LanguageSkillCodeValues,
  type LanguageProficiency,
  type LanguageSkillCode,
  type ProfileLanguageSkillDto,
  type UpdateProfileDto,
} from '@paladin-monorepo/common';
import { SuccessIndicator } from '@/components/ui/success-indicator';
import { Trash2 } from 'lucide-react';

export function LanguageSkillsCard() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [showSuccess, setShowSuccess] = useState(false);
  const hideTimeoutRef = useRef<number | null>(null);
  const [rows, setRows] = useState<ProfileLanguageSkillDto[]>([]);
  const initialized = useRef(false);

  const { data: profile, isLoading } = useQuery({
    queryKey: ['profile'],
    queryFn: () => profileApi.getProfile(),
  });

  useEffect(() => {
    if (!profile || initialized.current) return;
    initialized.current = true;
    setRows(
      profile.languageSkills?.length
        ? profile.languageSkills.map((skill) => ({ ...skill }))
        : [],
    );
  }, [profile]);

  const mutation = useMutation({
    mutationFn: (dto: UpdateProfileDto) => profileApi.updateProfile(dto),
    onSuccess: (data) => {
      setRows(data.languageSkills?.length ? data.languageSkills.map((skill) => ({ ...skill })) : []);
      onProfileMutationSuccess(queryClient, data);
      setShowSuccess(true);
      if (hideTimeoutRef.current) {
        window.clearTimeout(hideTimeoutRef.current);
      }
      hideTimeoutRef.current = window.setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
    },
    onError: () => {
      toast.error(t('profile.languageSkills.saveError'));
    },
  });

  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        window.clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  const save = () => {
    const codes = rows.map((row) => row.languageCode);
    if (new Set(codes).size !== codes.length) {
      toast.error(t('profile.languageSkills.duplicateError'));
      return;
    }
    if (rows.some((row) => !row.languageCode || !row.proficiency)) {
      toast.error(t('profile.languageSkills.missingProficiencyError'));
      return;
    }
    mutation.mutate({ languageSkills: rows });
  };

  const updateRow = (index: number, patch: Partial<ProfileLanguageSkillDto>) => {
    setRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...patch };
      return next;
    });
  };

  const removeRow = (index: number) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const addRow = () => {
    const used = new Set(rows.map((row) => row.languageCode));
    const nextCode = LanguageSkillCodeValues.find((code) => !used.has(code));
    if (!nextCode) {
      toast.error(t('profile.languageSkills.duplicateError'));
      return;
    }
    setRows((prev) => [...prev, { languageCode: nextCode, proficiency: 'professional_working' }]);
  };

  const usedCodes = (currentIndex: number) =>
    new Set(rows.filter((_, i) => i !== currentIndex).map((row) => row.languageCode));

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {t('profile.languageSkills.title')}
          <SuccessIndicator visible={showSuccess} />
        </CardTitle>
        <CardDescription>{t('profile.languageSkills.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <div>{t('generic.loading')}</div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('profile.languageSkills.empty')}</p>
        ) : (
          <div className="space-y-4">
            {rows.map((row, index) => (
              <div
                key={`${index}-${row.languageCode}`}
                className="grid gap-3 sm:grid-cols-[1fr_1fr_auto] items-end border-b border-border pb-4 last:border-0"
              >
                <div className="space-y-1">
                  <Label>{t('profile.languageSkills.languageLabel')}</Label>
                  <Select
                    value={row.languageCode}
                    onValueChange={(value) =>
                      updateRow(index, { languageCode: value as LanguageSkillCode })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('profile.languageSkills.languagePlaceholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {LanguageSkillCodeValues.filter(
                        (code) => code === row.languageCode || !usedCodes(index).has(code),
                      ).map((code) => (
                        <SelectItem key={code} value={code}>
                          {t(`profile.languageSkills.languages.${code}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>{t('profile.languageSkills.proficiencyLabel')}</Label>
                  <Select
                    value={row.proficiency}
                    onValueChange={(value) =>
                      updateRow(index, { proficiency: value as LanguageProficiency })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={t('profile.languageSkills.proficiencyPlaceholder')}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {LanguageProficiencyValues.map((level) => (
                        <SelectItem key={level} value={level}>
                          {t(`profile.languageSkills.proficiency.${level}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeRow(index)}
                  aria-label={t('profile.languageSkills.remove')}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" onClick={addRow}>
            {t('profile.languageSkills.add')}
          </Button>
          <Button type="button" onClick={save} disabled={mutation.isPending}>
            {t('profile.languageSkills.save')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
